import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import envConfig from '../../../envConfig';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import { CrudType } from '../../../utility/types/CrudType';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import { SubscriptionChecklistType } from '../../../utility/types/SubscriptionChecklistType';

const initialState = {
  loading: true,
  loadingError: false,
  primaryChecklist: { checklistEntries: [] },
  secondaryChecklist: { checklistEntries: [] },
  guidedSetupUpgradeEligibility: false,
  guidedSetupAvailable: false,
  primaryCompletionStats: { totalItems: 0, completedItems: 0, completionPercentage: 0 },
  secondaryCompletionStats: { totalItems: 0, completedItems: 0, completionPercentage: 0 },
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  errors: [],
};

const selectSubscriptionChecklistState = (state) => state.subscriptionChecklist;

const selectSubscriptionChecklistLoading = createSelector(
  [selectSubscriptionChecklistState],
  (slice) => slice.loading,
);

const selectPrimaryChecklist = createSelector(
  [selectSubscriptionChecklistState],
  (slice) => slice.primaryChecklist,
);

const selectSecondaryChecklist = createSelector(
  [selectSubscriptionChecklistState],
  (slice) => slice.secondaryChecklist,
);

const selectPrimaryChecklistEntries = createSelector(
  [selectPrimaryChecklist],
  (checklist) => (checklist ? checklist.checklistEntries : []),
);

const selectSecondaryChecklistEntries = createSelector(
  [selectSecondaryChecklist],
  (checklist) => (checklist ? checklist.checklistEntries : []),
);

const selectPrimaryChecklistCompletionStats = createSelector(
  [selectSubscriptionChecklistState],
  (slice) => slice.primaryCompletionStats,
);

const selectSecondaryChecklistCompletionStats = createSelector(
  [selectSubscriptionChecklistState],
  (slice) => slice.secondaryCompletionStats,
);

const selectActiveChecklistType = createSelector(
  [selectPrimaryChecklist, selectSecondaryChecklist],
  (primaryChecklist, secondaryChecklist) => {
    if (!primaryChecklist) {
      return null;
    }
    if (!primaryChecklist.dateCompleted) {
      return SubscriptionChecklistType.PRIMARY_CHECKLIST;
    }

    if (!secondaryChecklist) {
      return null;
    }
    if (!secondaryChecklist.dateCompleted) {
      return SubscriptionChecklistType.SECONDARY_CHECKLIST;
    }

    return null;
  },
);

const fetchSubscriptionChecklists = createAsyncThunk(
  'securedApi/subscriptionChecklist/fetchByType',
  async (_, thunkAPI) => {
    try {
      const { activeSubscriptionId } = thunkAPI.getState().session.sessionInformation;
      const primaryResponse = await RaaAuthenticatedRemoteService.globalInstance().get(
        `${envConfig.apiUrl}/api/v1/subscription-checklist?subscriptionId=${activeSubscriptionId}&typeId=1`,
      );

      const secondaryResponse = await RaaAuthenticatedRemoteService.globalInstance()
        .get(
          `${envConfig.apiUrl}/api/v1/subscription-checklist?subscriptionId=${activeSubscriptionId}&typeId=2`,
        );

      return {
        primaryChecklist: primaryResponse.checklist,
        secondaryChecklist: secondaryResponse ? secondaryResponse.checklist : null,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error: {
          name: error.name,
          message: error.message,
          data: error.data,
        },
      });
    }
  },
);

const setChecklistEntryComplete = createAsyncThunk(
  'securedApi/subscriptionChecklist/setEntryComplete',
  async ({ checklistId, checklistItemId }, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance().post(
        `${envConfig.apiUrl}/api/v1/subscription-checklist/set-complete`,
        {
          data: {
            subscriptionChecklistId: checklistId,
            subscriptionChecklistItemId: checklistItemId,
          },
        },
      );
      return {
        checklistItemId,
        entry: response.entry,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        checklistItemId,
        error: {
          name: error.name,
          message: error.message,
          data: error.data,
        },
      });
    }
  },
);

const fetchChecklistCompletionStats = createAsyncThunk(
  'securedApi/subscriptionChecklist/fetchCompletionStats',
  async (_, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance().get(
        `${envConfig.apiUrl}/api/v1/subscription-checklist/completion-percentage`,
      );

      return {
        primary: response.primary,
        secondary: response.secondary,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        error: {
          name: error.name,
          message: error.message,
          data: error.data,
        },
      });
    }
  },
);

const subscriptionChecklistSlice = createSlice({
  name: 'subscriptionChecklist',
  initialState,
  reducers: {
    clearSubscriptionChecklistSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionChecklists.pending, (state) => {
        state.loading = true;
        state.loadingError = false;
      })
      .addCase(fetchSubscriptionChecklists.fulfilled, (state, action) => {
        if (action.payload.primaryChecklist) {
          state.primaryChecklist = action.payload.primaryChecklist;
        }
        if (action.payload.secondaryChecklist) {
          state.secondaryChecklist = action.payload.secondaryChecklist;
        }
        state.loading = false;
        state.loadingError = false;
      })
      .addCase(fetchSubscriptionChecklists.rejected, (state, action) => {
        state.loading = false;
        state.loadingError = true;
        state.errors = action.payload;
      })
      .addCase(setChecklistEntryComplete.pending, (state, action) => {
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
        state.errors = [];
        const { checklistItemId } = action.meta.arg;
        const checklist = state.primaryChecklist;

        if (checklist && checklist.checklistEntries) {
          const itemToUpdate = checklist.checklistEntries.find(
            (entry) => entry.itemId === checklistItemId,
          );

          if (itemToUpdate) {
            itemToUpdate.dateCompleted = new Date().toISOString();
          }
        }
      })
      .addCase(setChecklistEntryComplete.fulfilled, (state) => {
        state.crudStatus = CrudStatus.SUCCESS;
      })
      .addCase(setChecklistEntryComplete.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload.error;

        const { checklistItemId } = action.payload;
        const checklist = state.primaryChecklist;

        if (checklist && checklist.checklistEntries) {
          const itemToRevert = checklist.checklistEntries.find(
            (entry) => entry.itemId === checklistItemId,
          );

          if (itemToRevert) {
            itemToRevert.dateCompleted = null;
          }
        }
      })
      .addCase(fetchChecklistCompletionStats.pending, () => {
      })
      .addCase(fetchChecklistCompletionStats.fulfilled, (state, action) => {
        if (action.payload.primary) {
          state.primaryCompletionStats = action.payload.primary;
        }
        if (action.payload.secondary) {
          state.secondaryCompletionStats = action.payload.secondary;
        }
      })
      .addCase(fetchChecklistCompletionStats.rejected, (state, action) => {
        state.errors = action.payload ? action.payload.error : [];
      });
  },
});

// Export actions and selects
const { actions, reducer } = subscriptionChecklistSlice;
export const { clearSubscriptionChecklistSlice } = actions;
export {
  fetchSubscriptionChecklists,
  setChecklistEntryComplete,
  selectPrimaryChecklist,
  selectSecondaryChecklist,
  selectPrimaryChecklistEntries,
  selectSecondaryChecklistEntries,
  selectSubscriptionChecklistLoading,
  fetchChecklistCompletionStats,
  selectPrimaryChecklistCompletionStats,
  selectSecondaryChecklistCompletionStats,
  selectActiveChecklistType,
};
export default reducer;
